import React, { useState } from "react";
import {
  Box,
  IconButton,
  Paper,
  styled,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Modal,
  Button,
} from "@mui/material";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";

import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import moment from "moment";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.grey[200],
    color: theme.palette.grey[600],
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(even)": {
    backgroundColor: theme.palette.action.selected,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const ProcessosAnexos = ({ attachments, onDelete }) => {
  const [deleteStep, setDeleteStep] = useState(0); // 0: nenhum, 1: primeira, 2: segunda
  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleOpenAttachment = (path) => {
    if (path) {
      window.open(`${process.env.REACT_APP_BACKEND_URL}/anexos/${path}`, "_blank");
    }
  };

  // Função para baixar o arquivo diretamente
  const handleDownloadAttachment = (path, nomeAnexo) => {
    if (path) {
      const url = `${process.env.REACT_APP_BACKEND_URL}/anexos/${path}`;
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', nomeAnexo || 'anexo.pdf');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleDeleteAttachment = (attachment) => {
    setDeleteTarget(attachment);
    setDeleteStep(1);
  };

  const handleDeleteConfirm = () => {
    if (deleteStep === 1) {
      setDeleteStep(2);
      return;
    }
    if (deleteStep === 2 && deleteTarget) {
      if (onDelete) {
        onDelete(deleteTarget);
      }
      setDeleteStep(0);
      setDeleteTarget(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteStep(0);
    setDeleteTarget(null);
  };

  return (
    <>
      <Paper sx={{ my: 1, p: 2, width: '100%' }} elevation={0}>
        <Typography mb={2} variant="h6">
          Anexos
        </Typography>
        <TableContainer component={Box} sx={{ width: '100%' }}>
          <Table size="small" sx={{ width: "100%" }}>
            <TableHead>
              <TableRow>
                <StyledTableCell></StyledTableCell>
                <StyledTableCell align="left">Nome do arquivo</StyledTableCell>
                <StyledTableCell align="left">Data de upload</StyledTableCell>
                <StyledTableCell align="left">Tipo</StyledTableCell>
                <StyledTableCell align="left">Descrição</StyledTableCell>
                <StyledTableCell align="center">Excluir</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {attachments?.length > 0 ? (
                attachments.map((attachment) => (
                  <StyledTableRow key={attachment?.id || attachment?.nome_anexo}>
                    <StyledTableCell>
                      <Tooltip title="Visualizar PDF">
                        <IconButton
                          edge="end"
                          aria-label="visualizar-pdf"
                          onClick={() => handleOpenAttachment(attachment?.path)}
                          component="a"
                          href={`${process.env.REACT_APP_BACKEND_URL}/anexos/${attachment?.path}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <VisibilityIcon color="primary" />
                        </IconButton>
                      </Tooltip>
                    </StyledTableCell>
                    <StyledTableCell>
                      {attachment?.nome_anexo ?? "- -"}
                    </StyledTableCell>
                    <StyledTableCell>
                      {moment
                        .utc(attachment?.createdAt)
                        .format("DD/MM/YYYY [-] HH:mm")}
                    </StyledTableCell>
                    <StyledTableCell>
                      {attachment?.tipo === 'LOGO' ? 'Logo' : attachment?.tipo === 'PETICAO_DO_INPI' ? 'Petição do INPI' : (attachment?.tipo || '-')}
                    </StyledTableCell>
                    <StyledTableCell>
                      {attachment?.descricao || '-'}
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <Tooltip title="Excluir anexo">
                        <IconButton
                          edge="end"
                          aria-label="excluir-anexo"
                          onClick={() => handleDeleteAttachment(attachment)}
                          size="small"
                        >
                          <CloseIcon color="error" />
                        </IconButton>
                      </Tooltip>
                    </StyledTableCell>
                  </StyledTableRow>
                ))
              ) : (
                <Box width={"100%"} p={2} textAlign={"center"}>
                  Nenhuma publicação
                </Box>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      {/* Modal de confirmação dupla para exclusão */}
      <Modal open={deleteStep > 0} onClose={handleDeleteCancel}>
        <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 360, bgcolor: 'background.paper', border: '2px solid #000', boxShadow: 24, p: 4, textAlign: 'center' }}>
          <Typography variant="h6" mb={2}>
            {deleteStep === 1 ? 'Confirmar exclusão' : 'Atenção!'}
          </Typography>
          <Typography mb={3}>
            {deleteStep === 1
              ? 'Tem certeza que deseja deletar este anexo?'
              : 'Esta ação é irreversível. Deseja realmente excluir?'}
          </Typography>
          <Box display="flex" justifyContent="center" gap={2}>
            <Button onClick={handleDeleteCancel} variant="outlined" color="primary">
              Cancelar
            </Button>
            <Button onClick={handleDeleteConfirm} variant="contained" color={deleteStep === 2 ? 'error' : 'primary'}>
              {deleteStep === 1 ? 'Sim, continuar' : 'Excluir'}
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default React.memo(ProcessosAnexos);
